<script lang="ts">
import { defineComponent, ref } from 'vue';
import { throttled, isTextTruncatedEl } from '@/lib/functions';
import { assert, Assertions } from '@/lib/assert';

export default defineComponent({
  name: 'TextTruncated',

  props: {
    as: {
      type: String,
      default: () => 'p',
    },

    text: {
      required: true,
      type: String,
    },

    tooltipProps: {
      type: Object,
      default: () => ({}),
    },
  },

  setup() {
    const textTruncatedEl = ref<HTMLElement | null>(null);
    const overflowing = ref(false);
    const width = ref(0);

    const onResize = throttled(async ([entry]: ResizeObserverEntry[]) => {
      requestAnimationFrame(() => {
        assert(entry.target instanceof HTMLElement, 'Expected entry.target to be instanceof HTMLElement');

        width.value = entry.contentBoxSize[0].inlineSize;

        if (isTextTruncatedEl(entry.target, width.value)) {
          overflowing.value = true;
        }
        else {
          overflowing.value = false;
        }
      });
    }, 100);

    const observer = ref(new ResizeObserver(onResize));

    return { textTruncatedEl, overflowing, observer, width };
  },

  computed: {
    defaultTooltipProps() {
      return {
        location: 'top',
        openDelay: 250,
        maxWidth: this.width * 3,
        text: this.text,
      } as const;
    },
  },

  mounted() {
    Assertions.defined(this.textTruncatedEl, 'textTruncatedEl');
    this.observer.observe(this.textTruncatedEl);
  },

  beforeUnmount() {
    Assertions.defined(this.textTruncatedEl, 'textTruncatedEl');
    if (this.observer) this.observer.disconnect();
  },
});
</script>

<template>
  <component :is="as" ref="textTruncatedEl" :class="{ 'tw:truncate': overflowing }">
    {{ text }}
    <v-tooltip v-if="overflowing" v-bind="{ ...defaultTooltipProps, ...tooltipProps }" activator="parent" />
  </component>
</template>
